import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { getCurrentStats, getAnalysisStats } from 'src/utils/analysisStats'

// Analysis statistics shared by the navbar summary (layouts/dashboard/StatsSummary)
// and the statistics panel it opens (AnalysisHistoryChart). Both read the same
// react-query keys, so opening the panel is instant and nothing is fetched twice.
// There is no polling: data is refreshed when it is older than STALE_TIME and the
// panel mounts, or when a new analysis is started (see views/AnalysisView/index.js).

// RNA analyses are counted as their own kind (and no longer inside WGS, WES or CES).
// A server that does not send the RNA key yet reads as 0 RNA, so totals are unchanged.
export const ANALYSIS_KINDS = ['VCF', 'WholeGenome', 'WholeExome', 'ClinicalExome', 'CarrierAnalysis', 'RNA']

export const ANALYSIS_KIND_SHORT_NAMES = {
  VCF: 'VCF',
  WholeGenome: 'WGS',
  WholeExome: 'WES',
  ClinicalExome: 'CES',
  CarrierAnalysis: 'Carrier',
  RNA: 'RNA',
}

// Full names, as the pie legend and the column tooltip show them.
export const ANALYSIS_KIND_LABELS = {
  VCF: 'VCF',
  WholeGenome: 'Whole Genome',
  WholeExome: 'Whole Exome',
  ClinicalExome: 'Clinical Exome',
  CarrierAnalysis: 'Carrier Analysis',
  RNA: 'RNA',
}

// Trend window fetched once for the summary and the panel. It is one of the ranges
// the trend endpoint accepts (14, 30 or 45; 31 is rejected) and always covers the
// whole current month. The chart's 14 and 30 day ranges are cut from it instead of
// being fetched again (the endpoint returns one row per day, newest first, today
// included).
export const BASE_TREND_DAYS = 45

// Days drawn in the navbar sparkline.
export const SPARKLINE_DAYS = 14

const STALE_TIME = 5 * 60 * 1000

export const ANALYSIS_STATS_KEY = 'analysis-stats'

export const dayTotal = (day) => ANALYSIS_KINDS.reduce((sum, kind) => sum + (day?.[kind] || 0), 0)

const numberFormat = new Intl.NumberFormat('en-US')
export const formatCount = (value) => numberFormat.format(value || 0)

export const useAnalysisHistoryStats = () =>
  useQuery([ANALYSIS_STATS_KEY, 'history'], getCurrentStats, { staleTime: STALE_TIME })

const useTrendQuery = (days, enabled = true) =>
  useQuery([ANALYSIS_STATS_KEY, 'trend', days], () => getAnalysisStats(days), {
    staleTime: STALE_TIME,
    keepPreviousData: true,
    enabled,
  })

// Daily rows for the last `days` days, newest first.
export const useAnalysisTrendStats = (days) => {
  const ownWindow = days > BASE_TREND_DAYS
  const base = useTrendQuery(BASE_TREND_DAYS)
  const own = useTrendQuery(days, ownWindow)
  const query = ownWindow ? own : base
  const rows = Array.isArray(query.data) ? query.data : null
  // Memoised so the chart only redraws when the rows really change.
  const data = useMemo(() => (rows && !ownWindow ? rows.slice(0, days) : rows), [rows, ownWindow, days])
  return {
    data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
  }
}

const isInCurrentMonth = (isoDate, now) => {
  const [year, month] = String(isoDate || '')
    .split('-')
    .map(Number)
  return year === now.getFullYear() && month === now.getMonth() + 1
}

// Numbers for the navbar summary and the panel's Total / This month / Top category tiles.
export const useAnalysisStatsSummary = () => {
  const history = useAnalysisHistoryStats()
  const trend = useAnalysisTrendStats(BASE_TREND_DAYS)

  const counts = history.data || null
  const total = counts ? ANALYSIS_KINDS.reduce((sum, kind) => sum + (counts[kind] || 0), 0) : null

  let topCategory = null
  if (counts && total > 0) {
    topCategory = ANALYSIS_KINDS.reduce((best, kind) => ((counts[kind] || 0) > (counts[best] || 0) ? kind : best))
  }

  const rows = trend.data
  const { thisMonth, sparkline } = useMemo(() => {
    if (!rows) return { thisMonth: null, sparkline: [] }
    const now = new Date()
    return {
      thisMonth: rows.filter((day) => isInCurrentMonth(day.date, now)).reduce((sum, day) => sum + dayTotal(day), 0),
      // Oldest first, so the sparkline reads left to right like the chart.
      sparkline: rows.slice(0, SPARKLINE_DAYS).map(dayTotal).reverse(),
    }
  }, [rows])

  return {
    total,
    thisMonth,
    topCategory,
    sparkline,
    isLoading: history.isLoading || trend.isLoading,
    isError: history.isError && trend.isError,
  }
}
